import Image from 'next/image';
import React from 'react';
import { Voice } from 'types';

import styles from './voicePicker.module.css';

import clsxm from '@/lib/clsxm';

import { getVoiceImageUrl } from '@/utils/voices';

/** A stable gradient orb for voices without a portrait (the user's own). */
const orbBackground = (seed: string) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  const second = (hue + 50 + (Math.abs(hash >> 8) % 70)) % 360;
  return `radial-gradient(circle at 32% 28%, hsl(${hue} 100% 86%) 0%, hsl(${hue} 85% 66%) 30%, hsl(${second} 72% 48%) 70%, hsl(${second} 70% 34%) 100%)`;
};

export const Equalizer = ({ className }: { className?: string }) => (
  <span className={clsxm(styles.equalizer, className)} aria-hidden='true'>
    <span />
    <span />
    <span />
  </span>
);

const PlayGlyph = ({ className }: { className?: string }) => (
  <svg viewBox='0 0 24 24' fill='currentColor' className={className}>
    <path d='M8 5.14v13.72a1 1 0 0 0 1.52.85l11.05-6.86a1 1 0 0 0 0-1.7L9.52 4.29A1 1 0 0 0 8 5.14Z' />
  </svg>
);

const PauseGlyph = ({ className }: { className?: string }) => (
  <svg viewBox='0 0 24 24' fill='currentColor' className={className}>
    <rect x='6' y='5' width='4' height='14' rx='1.2' />
    <rect x='14' y='5' width='4' height='14' rx='1.2' />
  </svg>
);

interface VoiceAvatarProps {
  voice: Voice;
  size?: 'sm' | 'md';
  playing?: boolean;
  /** Without it the avatar is a plain picture (no preview to play). */
  onTogglePlay?: () => void;
  playLabel?: string;
}

const VoiceAvatar = ({
  voice,
  size = 'md',
  playing = false,
  onTogglePlay,
  playLabel,
}: VoiceAvatarProps) => {
  const dimension = size === 'sm' ? 32 : 44;
  const art = voice.image ? (
    <Image
      src={getVoiceImageUrl(voice.image)}
      alt=''
      width={dimension}
      height={dimension}
      className='h-full w-full object-cover'
      loading='lazy'
    />
  ) : (
    <span
      className='block h-full w-full'
      style={{ backgroundImage: orbBackground(voice.voice_id) }}
    />
  );
  const frame = clsxm(
    'relative block shrink-0 overflow-hidden rounded-full bg-gray-100 ring-1 ring-black/5 dark:bg-slate-800 dark:ring-white/10',
    size === 'sm' ? 'h-8 w-8' : 'h-11 w-11',
  );

  if (!onTogglePlay) {
    return <span className={frame}>{art}</span>;
  }

  return (
    <button
      type='button'
      onClick={(event) => {
        event.stopPropagation();
        onTogglePlay();
      }}
      aria-label={playLabel}
      aria-pressed={playing}
      className={clsxm(
        frame,
        'group/avatar focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900',
        playing &&
          'ring-2 ring-primary-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-900',
      )}
    >
      {art}
      <span
        className={clsxm(
          'absolute inset-0 flex items-center justify-center bg-gray-950/55 text-white transition-opacity duration-150',
          playing
            ? 'opacity-100'
            : 'opacity-0 group-hover:opacity-100 group-focus-visible/avatar:opacity-100 [@media(hover:none)]:bg-gray-950/30 [@media(hover:none)]:opacity-100',
        )}
      >
        {playing ? (
          <>
            <Equalizer className='group-hover/avatar:hidden' />
            <PauseGlyph className='hidden h-4 w-4 group-hover/avatar:block' />
          </>
        ) : (
          <PlayGlyph className='h-4 w-4 translate-x-px' />
        )}
      </span>
    </button>
  );
};

export default VoiceAvatar;
